/**
 * One assessment component: what it is, what it weighs, and what it measures.
 *
 * @module components/studio/panels/AssessmentRow
 */

import React from "react";

import { AssessmentRequirementControls } from "@/components/shared/AssessmentRequirementControls";
import { CrossModuleAssessmentControls } from "@/components/shared/CrossModuleAssessmentControls";
import {
  ASSESSMENT_MODE_LABELS,
  ASSESSMENT_MODES,
  ASSESSMENT_TYPES,
  INTEGRITY_CONTROLS,
} from "@/components/studio/module-editor-model";
import type { ModuleEditorState } from "@/components/studio/useModuleEditor";
import {
  Checkbox,
  CheckboxCard,
  CheckboxChipGroup,
  Heading,
  IconButton,
  MutedText,
  useConfirm,
} from "@/components/ui";
import {
  FieldSet,
  FormField,
  FormGrid,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/ui/Form";
import type { MIMLO, Module, ModuleAssessment } from "@/types";

export function AssessmentRow({
  assessment,
  index,
  mimlos,
  moduleId,
  programmeModules,
  editor,
}: {
  assessment: ModuleAssessment;
  index: number;
  mimlos: readonly MIMLO[];
  /** The module this assessment belongs to, which coordinates any sharing. */
  moduleId: string;
  /** Every module of the programme, which a shared assessment can reach. */
  programmeModules: readonly Module[];
  editor: ModuleEditorState;
}): React.ReactElement {
  const confirm = useConfirm();
  const update = (change: (draft: ModuleAssessment) => void) =>
    editor.updateAssessment(assessment.id, change);

  const remove = async (): Promise<void> => {
    const proceed = await confirm({
      title: `Remove assessment ${index + 1}?`,
      body: "This removes the assessment and its module learning-outcome mappings.",
      confirmLabel: "Remove assessment",
      tone: "danger",
      "data-testid": "module-remove-assessment-confirm",
    });
    if (proceed) {
      editor.removeAssessment(assessment.id);
    }
  };

  return (
    <div className="py-5" data-testid={`module-workspace-assessment-${index + 1}`}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <Heading level={4}>Assessment {index + 1}</Heading>
        <IconButton
          icon="trash"
          variant="ghost"
          aria-label={`Remove assessment ${index + 1}`}
          onClick={remove}
        />
      </div>
      <FormGrid columns={4}>
        <FormField
          label="Title"
          htmlFor={`module-assessment-title-${assessment.id}`}
          helpText="Name the assessment as it appears to learners and in validation records."
          className="@md:col-span-2 @4xl:col-span-4"
        >
          <TextField
            id={`module-assessment-title-${assessment.id}`}
            value={assessment.title ?? ""}
            onChange={(value) =>
              update((draft) => {
                draft.title = value;
              })
            }
            data-testid={`module-assessment-title-${index + 1}`}
          />
        </FormField>
        <FormField
          label="Type"
          htmlFor={`module-assessment-type-${assessment.id}`}
          helpText="Select the assessment method used for this component."
        >
          <SelectField
            id={`module-assessment-type-${assessment.id}`}
            value={assessment.type}
            onChange={(value) =>
              update((draft) => {
                draft.type = value;
              })
            }
            options={ASSESSMENT_TYPES.map((type) => ({ value: type, label: type }))}
          />
        </FormField>
        <FormField
          label="Weight"
          htmlFor={`module-assessment-weight-${assessment.id}`}
          helpText="Set the percentage contribution of this assessment toward module completion."
        >
          <TextField
            id={`module-assessment-weight-${assessment.id}`}
            type="number"
            min={0}
            max={100}
            value={assessment.weighting ?? assessment.weight ?? 0}
            onChange={(value) =>
              update((draft) => {
                draft.weighting = Math.min(100, Math.max(0, Number(value) || 0));
              })
            }
            suffix="%"
          />
        </FormField>
        <FormField
          label="Mode"
          htmlFor={`module-assessment-mode-${assessment.id}`}
          helpText="Choose how learners complete this assessment (for example online or on campus)."
        >
          <SelectField
            id={`module-assessment-mode-${assessment.id}`}
            value={assessment.mode ?? "Online"}
            onChange={(value) =>
              update((draft) => {
                draft.mode = value;
              })
            }
            options={ASSESSMENT_MODES.map((mode) => ({
              value: mode,
              label: ASSESSMENT_MODE_LABELS[mode] ?? mode,
            }))}
            data-testid={`module-assessment-mode-${index + 1}`}
          />
        </FormField>
        <FormField
          label="Assessment week"
          htmlFor={`module-assessment-week-${assessment.id}`}
          helpText="Enter programme week number when the assessment is scheduled."
        >
          <TextField
            id={`module-assessment-week-${assessment.id}`}
            type="number"
            min={1}
            value={assessment.indicativeWeek ?? ""}
            onChange={(value) =>
              update((draft) => {
                draft.indicativeWeek = value ? Math.max(1, Number(value)) : undefined;
              })
            }
            data-testid={`module-assessment-week-${index + 1}`}
          />
        </FormField>
      </FormGrid>

      <CheckboxCard
        id={`module-assessment-outside-teaching-${assessment.id}`}
        className="mt-4"
        label="This assessment occurs outside the teaching period"
        description="Select this when the assessment takes place after scheduled teaching. The assessment week may then be later than the module's teaching weeks."
        checked={assessment.outsideTeachingPeriod === true}
        onChange={(checked) =>
          update((draft) => {
            draft.outsideTeachingPeriod = checked;
          })
        }
        data-testid={`module-assessment-outside-teaching-${index + 1}`}
      />

      <AssessmentRequirementControls
        assessment={assessment}
        testIdPrefix={`module-assessment-requirement-${index + 1}`}
        onChange={(field, value) =>
          update((draft) => {
            draft[field] = value as never;
          })
        }
      />

      <CrossModuleAssessmentControls
        assessment={assessment}
        coordinatingModuleId={moduleId}
        modules={[...programmeModules]}
        testIdPrefix={`module-assessment-cross-${assessment.id}`}
        weightOf={(participantId) => editor.assessmentLedger.entriesFor(participantId)}
        onChange={(participatingModules) =>
          update((draft) => {
            draft.participatingModules = participatingModules;
          })
        }
      />

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        {mimlos.length ? (
          <CheckboxChipGroup
            legend="Measures outcomes"
            options={mimlos.map((mimlo, mimloIndex) => ({
              value: mimlo.id,
              label: `Outcome ${mimloIndex + 1}`,
              description: mimlo.text || "No outcome wording has been added yet.",
              "data-testid": `module-assessment-mimlo-option-${mimloIndex + 1}`,
            }))}
            value={assessment.mimloIds ?? []}
            onChange={(next) => {
              const nextSet = new Set(next);
              const current = new Set(assessment.mimloIds ?? []);
              mimlos.forEach((mimlo) => {
                const wasOn = current.has(mimlo.id);
                const isOn = nextSet.has(mimlo.id);
                if (wasOn !== isOn) {
                  editor.toggleAssessmentMimlo(assessment.id, mimlo.id, isOn);
                }
              });
            }}
          />
        ) : (
          <FieldSet legend="Measures outcomes">
            <MutedText>Add module outcomes first.</MutedText>
          </FieldSet>
        )}
        <FieldSet legend="Integrity controls" columns={2}>
          {INTEGRITY_CONTROLS.map((control) => (
            <Checkbox
              key={control.key}
              id={`module-assessment-integrity-${assessment.id}-${control.key}`}
              label={control.label}
              checked={Boolean(assessment.integrity?.[control.key])}
              onChange={(checked) =>
                update((draft) => {
                  draft.integrity = { ...draft.integrity };
                  draft.integrity[control.key] = checked;
                })
              }
              data-testid={`module-assessment-integrity-${index + 1}-${control.key}`}
            />
          ))}
        </FieldSet>
      </div>

      <div className="mt-5">
        <FormField
          label="Notes"
          htmlFor={`module-assessment-notes-${assessment.id}`}
          helpText="Add instructions, constraints, or context that reviewers and builders should see with this assessment."
        >
          <TextAreaField
            id={`module-assessment-notes-${assessment.id}`}
            className="min-h-24"
            value={assessment.notes ?? ""}
            onChange={(value) =>
              update((draft) => {
                draft.notes = value;
              })
            }
          />
        </FormField>
      </div>
    </div>
  );
}

export default AssessmentRow;
